import { useToast } from '@chakra-ui/react';
import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { tomApi } from '../api/tomApi';
import { isDevelopmentOrTesting } from '../../../constants/global.constants';
import { tomQueryKeys } from '../queries';
import type { TomConversationMessagesPage, TomStreamEvent } from '../types';
import {
	isTerminalTomMessage,
	useTomMessageOverlays,
} from './useTomMessageOverlays';

type AbortReason = 'stop' | 'switch' | 'unmount' | null;

type RunStreamOptions = {
	targetConversationId: number;
	content?: string;
	assistantMessageId?: number;
	onAccepted?: VoidFunction;
};

const wait = (milliseconds: number) =>
	new Promise(resolve => window.setTimeout(resolve, milliseconds));

export const useTomChatController = () => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [isBusy, setIsBusy] = useState(false);
	const [canStop, setCanStop] = useState(false);
	const [streamConversationId, setStreamConversationId] = useState<
		number | null
	>(null);
	const [activeAssistantMessageId, setActiveAssistantMessageId] = useState<
		number | null
	>(null);
	const [retryingMessageId, setRetryingMessageId] = useState<number>();
	const [progressLabel, setProgressLabel] = useState('Thinking');
	const {
		messageOverlays,
		upsertMessage,
		upsertMessages,
		appendDelta,
		removeResolvedMessageOverlays,
		clearConversationOverlays,
	} = useTomMessageOverlays();
	const activeControllerRef = useRef<AbortController | null>(null);
	const activeAssistantMessageIdRef = useRef<number | null>(null);
	const abortReasonRef = useRef<AbortReason>(null);
	const busyRef = useRef(false);

	const invalidateConversation = useCallback(
		(targetConversationId: number) => {
			void queryClient.invalidateQueries({
				queryKey: tomQueryKeys.messages(targetConversationId),
			});
			void queryClient.invalidateQueries({
				queryKey: tomQueryKeys.conversations(),
			});
		},
		[queryClient],
	);

	const reconcileAssistantMessage = useCallback(
		async (targetConversationId: number, assistantMessageId: number) => {
			for (let attempt = 0; attempt < 10; attempt += 1) {
				try {
					const page = await tomApi.getMessages(
						targetConversationId,
						null,
					);
					upsertMessages(targetConversationId, page.items);
					const assistantMessage = page.items.find(
						message => message.id === assistantMessageId,
					);

					if (
						assistantMessage &&
						isTerminalTomMessage(assistantMessage)
					) {
						break;
					}
				} catch {
					break;
				}

				await wait(500);
			}

			invalidateConversation(targetConversationId);
		},
		[invalidateConversation, upsertMessages],
	);

	const applyConversationSummary = useCallback(
		(
			targetConversationId: number,
			conversation: TomConversationMessagesPage['conversation'],
		) => {
			queryClient.setQueryData<
				InfiniteData<TomConversationMessagesPage, string | null>
			>(tomQueryKeys.messages(targetConversationId), current =>
				current
					? {
							...current,
							pages: current.pages.map(page => ({
								...page,
								conversation,
							})),
					  }
					: current,
			);
		},
		[queryClient],
	);

	const handleStreamEvent = useCallback(
		(
			targetConversationId: number,
			event: TomStreamEvent,
			onAccepted?: VoidFunction,
		) => {
			switch (event.type) {
				case 'accepted':
					onAccepted?.();
					setCanStop(true);
					if (event.userMessage) {
						upsertMessage(targetConversationId, event.userMessage);
					}
					activeAssistantMessageIdRef.current =
						event.assistantMessage.id;
					setActiveAssistantMessageId(event.assistantMessage.id);
					upsertMessage(targetConversationId, event.assistantMessage);
					setProgressLabel('Thinking');
					void queryClient.invalidateQueries({
						queryKey: tomQueryKeys.conversations(),
					});
					break;
				case 'progress':
					setProgressLabel(event.label);
					break;
				case 'delta':
					appendDelta(
						targetConversationId,
						event.assistantMessageId,
						event.content,
					);
					break;
				case 'completed':
					setCanStop(false);
					upsertMessage(targetConversationId, {
						...event.assistantMessage,
						diagnostics: isDevelopmentOrTesting
							? event.diagnostics
							: undefined,
					});
					applyConversationSummary(
						targetConversationId,
						event.conversation,
					);
					if (isDevelopmentOrTesting && event.diagnostics) {
						console.info(
							'Tom AI request diagnostics',
							event.diagnostics,
						);
					}
					invalidateConversation(targetConversationId);
					break;
				case 'error':
					setCanStop(false);
					upsertMessage(targetConversationId, {
						...event.assistantMessage,
						failure: event.error,
						diagnostics: isDevelopmentOrTesting
							? event.diagnostics
							: undefined,
					});
					if (isDevelopmentOrTesting && event.diagnostics) {
						console.error(
							'Tom AI request failed',
							event.diagnostics,
						);
					}
					invalidateConversation(targetConversationId);
					break;
			}
		},
		[
			appendDelta,
			applyConversationSummary,
			invalidateConversation,
			queryClient,
			upsertMessage,
		],
	);

	const runStream = useCallback(
		async ({
			targetConversationId,
			content,
			assistantMessageId,
			onAccepted,
		}: RunStreamOptions) => {
			if (busyRef.current) {
				return;
			}

			const controller = new AbortController();
			let wasAccepted = false;
			busyRef.current = true;
			activeControllerRef.current = controller;
			activeAssistantMessageIdRef.current = assistantMessageId || null;
			abortReasonRef.current = null;
			setIsBusy(true);
			setCanStop(false);
			setStreamConversationId(targetConversationId);
			setActiveAssistantMessageId(assistantMessageId || null);
			setRetryingMessageId(assistantMessageId);
			setProgressLabel('Thinking');

			try {
				const onEvent = (event: TomStreamEvent) =>
					handleStreamEvent(targetConversationId, event, () => {
						wasAccepted = true;
						onAccepted?.();
					});

				if (assistantMessageId) {
					await tomApi.retryMessage(
						targetConversationId,
						assistantMessageId,
						{ clientRequestId: uuidv4() },
						controller.signal,
						onEvent,
					);
				} else {
					await tomApi.streamMessage(
						targetConversationId,
						{
							clientRequestId: uuidv4(),
							content: content!,
						},
						controller.signal,
						onEvent,
					);
				}
			} catch (error) {
				const abortReason = abortReasonRef.current;
				const acceptedAssistantMessageId =
					activeAssistantMessageIdRef.current;

				if (abortReason === 'switch' && acceptedAssistantMessageId) {
					void reconcileAssistantMessage(
						targetConversationId,
						acceptedAssistantMessageId,
					);
				} else if (
					(abortReason === 'stop' || (!abortReason && wasAccepted)) &&
					acceptedAssistantMessageId
				) {
					await reconcileAssistantMessage(
						targetConversationId,
						acceptedAssistantMessageId,
					);
				}

				if (!abortReason) {
					toast({
						status: 'error',
						title: wasAccepted
							? 'Connection interrupted'
							: 'Unable to send message',
						description:
							error instanceof Error
								? error.message
								: 'Please try again.',
					});
				}
			} finally {
				if (activeControllerRef.current === controller) {
					activeControllerRef.current = null;
					activeAssistantMessageIdRef.current = null;
					abortReasonRef.current = null;
					busyRef.current = false;
					setIsBusy(false);
					setCanStop(false);
					setStreamConversationId(null);
					setActiveAssistantMessageId(null);
					setRetryingMessageId(undefined);
				}
			}
		},
		[handleStreamEvent, reconcileAssistantMessage, toast],
	);

	const stopResponse = useCallback(() => {
		if (!activeControllerRef.current) {
			return;
		}

		abortReasonRef.current = 'stop';
		setProgressLabel('Stopping response');
		activeControllerRef.current.abort();
	}, []);

	const cancelResponseForSwitch = useCallback(() => {
		if (!activeControllerRef.current) {
			return;
		}

		abortReasonRef.current = 'switch';
		activeControllerRef.current.abort();
	}, []);

	useEffect(
		() => () => {
			if (activeControllerRef.current) {
				abortReasonRef.current = 'unmount';
				activeControllerRef.current.abort();
			}
		},
		[],
	);

	return {
		isBusy,
		canStop,
		streamConversationId,
		activeAssistantMessageId,
		retryingMessageId,
		progressLabel,
		messageOverlays,
		removeResolvedMessageOverlays,
		clearConversationOverlays,
		runStream,
		stopResponse,
		cancelResponseForSwitch,
	};
};

export type TomChatSessionController = ReturnType<typeof useTomChatController>;
